/**
 * Runtime configuration
 * Loads configuration from window.__APP_CONFIG__ which is set in public/configuration.js
 */

interface AppConfig {
  apiBaseUrl: string;
  recaptchaSiteKey: string;
  loaderUrl: string;
  dhlApiKey: string;
  apiDocsUrl: string;
}

// Declare global window property
declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

/**
 * Get the API base URL from runtime config
 */
export function getApiBaseUrl(): string {
  // Runtime config from public/config.js
  if (window.__APP_CONFIG__?.apiBaseUrl && window.__APP_CONFIG__.apiBaseUrl !== '${VITE_API_BASE_URL}') {
    return window.__APP_CONFIG__.apiBaseUrl;
  }

  // Build-time environment variable
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // No fallback - configuration is required
  throw new Error('API base URL is not configured. Please set VITE_API_BASE_URL environment variable.');
}

/**
 * Get the reCAPTCHA site key from runtime config
 */
export function getRecaptchaSiteKey(): string {
  // Runtime config from public/config.js
  if (window.__APP_CONFIG__?.recaptchaSiteKey && window.__APP_CONFIG__.recaptchaSiteKey !== '${VITE_RECAPTCHA_SITE_KEY}') {
    return window.__APP_CONFIG__.recaptchaSiteKey;
  }

  // Build-time environment variable
  const siteKey = import.meta.env['VITE_RECAPTCHA_SITE_KEY'];
  if (siteKey) {
    return siteKey;
  }

  // No fallback - configuration is required
  throw new Error('reCAPTCHA site key is not configured. Please set VITE_RECAPTCHA_SITE_KEY environment variable.');
}

/**
 * Returns common HTTP headers to be added to every API request.
 * Pokud máme v sessionStorage uložené `sessionId` (z `POST /v1/activate`),
 * přidáme ho jako `X-Session`. BE ho validuje místo dříve používané cookie.
 */
export function getCommonHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'dhl-api-key': getDhlApiKey(),
  };
  try {
    const sessionId = sessionStorage.getItem('apw_session_id');
    if (sessionId) {
      headers['X-Session'] = sessionId;
    }
  } catch {
    // sessionStorage zakázáno — pokračuj bez X-Session
  }
  return headers;
}

/**
 * Get the DHL API key from runtime config
 */
export function getDhlApiKey(): string {
  // Runtime config from public/configuration.js
  if (window.__APP_CONFIG__?.dhlApiKey && window.__APP_CONFIG__.dhlApiKey !== '${VITE_DHL_API_KEY}') {
    return window.__APP_CONFIG__.dhlApiKey;
  }

  // Build-time environment variable
  if (import.meta.env['VITE_DHL_API_KEY']) {
    return import.meta.env['VITE_DHL_API_KEY'];
  }

  // No fallback - configuration is required
  throw new Error('DHL API key is not configured. Please set VITE_DHL_API_KEY or dhlApiKey in __APP_CONFIG__.');
}

/**
 * Get the widget loader URL from runtime config
 */
export function getWidgetLoaderUrl(): string {
  // Runtime config from public/configuration.js
  if (window.__APP_CONFIG__?.loaderUrl && window.__APP_CONFIG__.loaderUrl !== '${VITE_WIDGET_LOADER_URL}') {
    return window.__APP_CONFIG__.loaderUrl;
  }

  // Build-time environment variable
  if (import.meta.env['VITE_WIDGET_LOADER_URL']) {
    return import.meta.env['VITE_WIDGET_LOADER_URL'];
  }

  // No fallback - configuration is required
  throw new Error('Widget loader URL is not configured. Please set VITE_WIDGET_LOADER_URL or loaderUrl in __APP_CONFIG__.');
}

/**
 * Get the API documentation URL from runtime config
 */
export function getApiDocsUrl(): string {
  // Runtime config from public/configuration.js
  if (window.__APP_CONFIG__?.apiDocsUrl && window.__APP_CONFIG__.apiDocsUrl !== '${VITE_API_DOCS_URL}') {
    return window.__APP_CONFIG__.apiDocsUrl;
  }

  // Build-time environment variable
  if (import.meta.env['VITE_API_DOCS_URL']) {
    return import.meta.env['VITE_API_DOCS_URL'];
  }

  // No fallback - configuration is required
  throw new Error('API docs URL is not configured. Please set VITE_API_DOCS_URL or apiDocsUrl in __APP_CONFIG__.');
}
